import type { CSSObject } from '@emotion/react'

import type { GuideTourDefinition, GuideTourStep } from '../types'

export type GuideTourInProgressNotification = {
  tourId: string
  tourVersion: number
  kind: 'interrupted' | 'announcement'
  typeLabel: string
  title: string
  description: string
  currentStepIndex: number
  currentStepNumber: number
  stepCount: number
  currentStepRoute: string | null
}

export type GuideTourNotificationInteractionAction =
  | 'open-panel'
  | 'close-panel'
  | 'restart'
  | 'resume'
  | 'launch'

/** Step card and overlay (active tour). */
export type GuideTourStepInteractionAction =
  | 'start'
  | 'viewed'
  | 'next'
  | 'skipped'
  | 'previous'
  | 'complete'
  | 'dismiss'
  | 'interrupt'
  | 'restart'
  | 'target_interaction'

/** Emitted when the tour cannot run on the page (e.g. every step target missing). */
export type GuideTourPageInteractionAction = 'tour-unavailable'

export type GuideTourInteractionAction =
  | GuideTourNotificationInteractionAction
  | GuideTourStepInteractionAction
  | GuideTourPageInteractionAction

export const GUIDE_TOUR_TARGET_INTERACTION_ACTION =
  'target_interaction' as const satisfies GuideTourStepInteractionAction

export type GuideTourNotificationInteractionEvent = {
  action: GuideTourInteractionAction
  /**
   * Header notification panel: row related to the click.
   * Omitted when the interaction is on the active tour (card / overlay).
   */
  notificationItem?: GuideTourInProgressNotification | null
  /** Header panel: full list snapshot when the click happened. */
  notificationItems?: readonly GuideTourInProgressNotification[]
  /** Header panel: `notificationItems.length`. */
  notificationCount?: number
  /** Active tour: matches card copy “Passo X de Y” (1-based). */
  currentStep?: number
  /** Active tour: total steps (the Y in “Passo X de Y”). */
  totalStep?: number
  /** Position among tours on the same route (1-based). */
  tourCurrent?: number
  /** Total tours on the same route. */
  tourTotal?: number
  pathname?: string
  route?: string | null
  /** Stable tour identifier for analytics (also on `tour.id` when `tour` is present). */
  tourId?: string
  /** Tour JSON version (also on `tour.version` when `tour` is present). */
  tourVersion?: number
  /** Full tour JSON when the interaction happens on an active tour. */
  tour?: GuideTourDefinition
  /** Current step JSON when on an active tour. */
  step?: GuideTourStep
  /**
   * On card `action: 'start'` only: route auto-start vs opened after a button/`launch`.
   */
  autoStart?: boolean
  /**
   * Segment / analytics hint (e.g. Guide Tour Clicked `component_name`).
   * Set on `GuideTourButton` restart; omit on panel/header restarts until mapped.
   */
  componentName?: string
}

export type GuideTourNotificationsStyles = {
  wrapper?: CSSObject
  trigger?: CSSObject
  countBadge?: CSSObject
  panel?: CSSObject
  panelHeader?: CSSObject
  panelTitle?: CSSObject
  panelDescription?: CSSObject
  itemList?: CSSObject
  item?: CSSObject
  itemBody?: CSSObject
  itemType?: CSSObject
  itemTitle?: CSSObject
  itemDescription?: CSSObject
  itemActions?: CSSObject
  primaryAction?: CSSObject
  secondaryAction?: CSSObject
}

export type GuideTourNotificationsProps = {
  onInteraction?: (event: GuideTourNotificationInteractionEvent) => void
  /**
   * Optional style overrides merged on top of the default notification styles.
   * Pass only the slots you want to customize.
   */
  styles?: GuideTourNotificationsStyles
}
